import React, {createContext, useContext, useEffect, useState, ReactNode} from 'react';
import {SessionDataResponse, UserInfo} from '../api/rest/auth/dtos';
import AuthClientFactory from "../api/rest/auth/AuthClientFactory";

interface AuthContextType {
    user: UserInfo | null;
    isLoading: boolean;
    refreshSession: () => Promise<void>;
    clearSession: () => void;
    signout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({children}) => {
    const [user, setUser] = useState<UserInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const authClient = AuthClientFactory.getAuthClient();

    const refreshSession = async () => {
        try {
            const sessionData: SessionDataResponse = await authClient.getSessionData();
            setUser(sessionData.userInfo ?? null);
        } catch (error) {
            console.error('Error refreshing session:', error);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSession = () => {
        setUser(null);
    };

    const signout = async () => {
        try {
            await authClient.signout();
            clearSession();
        } catch (error) {
            console.error('Error signing out:', error);
            // Nawet jeśli wystąpi błąd, wyczyść sesję lokalnie
            clearSession();
        }
    };

    useEffect(() => {
        refreshSession();
    }, []);

    return (
        <AuthContext.Provider value={{user, isLoading, refreshSession, clearSession, signout}}>
            {children}
        </AuthContext.Provider>
    );
};

